/**
 * @file toast.ts
 * @module app/modules/_common/components/pop-up/toast
 * @description SweetAlert2 toast helpers for short-lived status messages.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.1
 * @version 2.1.2
 */

import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';
type ToastConfig<Options> = Options extends SweetAlertOptions
  ? Omit<Options, 'toast' | 'showConfirmButton' | 'icon'>
  : never;
export type ToastOptions = ToastConfig<SweetAlertOptions>;

const toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (toastElement) => {
    toastElement.onmouseenter = Swal.stopTimer;
    toastElement.onmouseleave = Swal.resumeTimer;
  },
});

export const showToast = (
  variant: ToastVariant,
  options: ToastOptions | string
): Promise<SweetAlertResult<unknown>> =>
  toast.fire({
    ...(typeof options === 'string' ? { title: options } : options),
    icon: variant,
  });

const Toast = {
  success: (options: ToastOptions | string) => showToast('success', options),
  info: (options: ToastOptions | string) => showToast('info', options),
  warning: (options: ToastOptions | string) => showToast('warning', options),
  error: (options: ToastOptions | string) => showToast('error', options),
};

export default Toast;